/**
 * Master–detail tag fleet: site summary cards (left) + searchable tag table (right).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BatteryAlert as LowBattIcon,
  Wifi as OnlineIcon,
  WifiOff as OfflineIcon,
  Inventory2 as InventoryIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { AppTextField } from '../design-system';
import { TableDetailSplitLayout } from '../ui/TableDetailSplitLayout';
import { EnhancedTable, type FilterConfig, type TableColumn } from '../ui/EnhancedTable';
import { scrollbarSx } from '../../theme/scrollbar';
import { useTheme } from '@mui/material/styles';
import { locationTelemetryService } from '../../services/LocationTelemetryService';
import type {
  LocationIntegrationVendorType,
  SiteTagFleetSummaryRow,
  SiteTagFleetTagRow,
  TagFleetPositionSource,
} from '@cnapse/shared/schemas.js';
import { formatDateTime, formatRelativeTime } from '../../utils/dateFormatting';
import { VENDOR_CAPABILITIES } from './vendorCapabilities';

const SITE_LIST_MIN_WIDTH = 260;
const SITE_LIST_MAX_WIDTH = 420;
const SITE_LIST_DEFAULT_WIDTH = 320;
const LOW_BATTERY_THRESHOLD = 20;

function formatTagFleetSourceLabel(
  positionSource: TagFleetPositionSource | null | undefined,
  vendorType: LocationIntegrationVendorType | null | undefined,
): string {
  if (positionSource === 'vendor') {
    if (vendorType && VENDOR_CAPABILITIES[vendorType]) {
      return VENDOR_CAPABILITIES[vendorType].label;
    }
    return 'Vendor integration';
  }
  return 'Cnapse Node';
}

function isVendorTag(row: SiteTagFleetTagRow): boolean {
  return row.positionSource === 'vendor';
}

function isUwbTag(row: SiteTagFleetTagRow): boolean {
  return row.positionSource !== 'vendor';
}

function siteKey(site: SiteTagFleetSummaryRow): string {
  return `${site.tenantId}::${site.siteId}`;
}

function MetadataCell({ text }: { text: string | null | undefined }) {
  const value = text?.trim() || '-';
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
      title={value === '-' ? undefined : value}
    >
      {value}
    </Box>
  );
}

interface SiteTagFleetCardProps {
  site: SiteTagFleetSummaryRow;
  selected: boolean;
  tenantLabel?: string | null;
  onSelect: () => void;
}

function SiteTagFleetCard({ site, selected, tenantLabel, onSelect }: SiteTagFleetCardProps) {
  const siteLabel = site.siteName ?? site.siteId;
  const showSiteId = siteLabel !== site.siteId;
  const hasGap = (site.neverSeenInventoryCount ?? 0) > 0;
  const needsAttention =
    site.tagsOfflineCount > 0 || site.lowBatteryCount > 0 || hasGap || site.vendorPollStatus === 'stale';

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        bgcolor: selected ? 'action.selected' : 'background.paper',
      }}
    >
      <CardActionArea onClick={onSelect}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap" useFlexGap>
            <Box minWidth={0} sx={{ flex: '1 1 100px' }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {siteLabel}
              </Typography>
              {showSiteId && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {site.siteId}
                </Typography>
              )}
              {tenantLabel && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {tenantLabel}
                </Typography>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: '1 1 auto', justifyContent: 'flex-end' }}
            >
              {needsAttention && !selected && (
                <Chip label="!" size="small" color="warning" sx={{ minWidth: 28 }} />
              )}
              <Chip
                size="small"
                variant="outlined"
                icon={<OnlineIcon />}
                label={`${site.tagsOnlineCount} online`}
                color="success"
              />
              {site.tagsOfflineCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<OfflineIcon />}
                  label={`${site.tagsOfflineCount} offline`}
                />
              )}
              {site.lowBatteryCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<LowBattIcon />}
                  label={`${site.lowBatteryCount} low`}
                  color="warning"
                />
              )}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {site.tagsSeenCount} seen
          </Typography>

          {(site.neverSeenInventoryCount ?? 0) > 0 && (
            <Stack direction="row" spacing={0.75} mt={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                icon={<InventoryIcon />}
                label={`${site.neverSeenInventoryCount} never seen`}
                color="warning"
                variant="outlined"
              />
            </Stack>
          )}

          {site.vendorPollStatus === 'stale' && (
            <Chip
              size="small"
              label="Vendor poll stale"
              color="warning"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export interface TagFleetSplitViewProps {
  sites: SiteTagFleetSummaryRow[];
  sitesLoading?: boolean;
  /** Resolve tenant display name for provider cards */
  tenantNameById?: (tenantId: string) => string | null;
  showTenantOnCards?: boolean;
  loadTags: (site: SiteTagFleetSummaryRow) => Promise<SiteTagFleetTagRow[]>;
  /** Provider ops: show manufacturer, model, and firmware columns */
  showDeviceMetadata?: boolean;
}

export default function TagFleetSplitView({
  sites,
  sitesLoading = false,
  tenantNameById,
  showTenantOnCards = false,
  loadTags,
  showDeviceMetadata = false,
}: TagFleetSplitViewProps) {
  const theme = useTheme();
  const [siteSearch, setSiteSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const [tags, setTags] = useState<SiteTagFleetTagRow[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagPage, setTagPage] = useState(1);
  const [tagPageSize, setTagPageSize] = useState(25);
  const [tagSearch, setTagSearch] = useState('');
  const [tagFilters, setTagFilters] = useState<Record<string, string>>({
    online: '',
    lowBattery: '',
    positionSource: '',
  });
  const [geofencingBusySerial, setGeofencingBusySerial] = useState<string | null>(null);

  const filteredSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((site) => {
      const tenantName = tenantNameById?.(site.tenantId) ?? '';
      const hay = `${site.siteName ?? ''} ${site.siteId} ${tenantName}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sites, siteSearch, tenantNameById]);

  const selectedSite = useMemo(
    () => sites.find((s) => siteKey(s) === selectedKey) ?? null,
    [sites, selectedKey],
  );

  const siteZoneAlarmsEnabled = selectedSite?.zoneAlarmsEnabled ?? false;
  const siteAllTagsZoneAlarms = selectedSite?.zoneAlarmsAllTagsEnabled ?? false;

  const handleGeofencingToggle = useCallback(async (row: SiteTagFleetTagRow, enabled: boolean) => {
    if (!selectedSite) return;
    setGeofencingBusySerial(row.serialNumber);
    try {
      await locationTelemetryService.patchTenantLocationTagGeofencing(selectedSite.siteId, row.serialNumber, enabled);
      setTags((prev) => prev.map((item) => (
        item.serialNumber === row.serialNumber ? { ...item, geofencingEnabled: enabled } : item
      )));
    } catch {
      // ApiService surfaces errors via makeRequest; avoid unhandled rejection on toggle.
    } finally {
      setGeofencingBusySerial(null);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (filteredSites.length === 0) {
      setSelectedKey(null);
      return;
    }
    if (!selectedKey || !filteredSites.some((s) => siteKey(s) === selectedKey)) {
      setSelectedKey(siteKey(filteredSites[0]!));
    }
  }, [filteredSites, selectedKey]);

  const fetchTags = useCallback(
    async (site: SiteTagFleetSummaryRow) => {
      setTagsLoading(true);
      setTags([]);
      try {
        const rows = await loadTags(site);
        setTags(rows);
      } catch {
        setTags([]);
      } finally {
        setTagsLoading(false);
      }
    },
    [loadTags],
  );

  useEffect(() => {
    if (!selectedSite) {
      setTags([]);
      return;
    }
    void fetchTags(selectedSite);
  }, [selectedSite, fetchTags]);

  useEffect(() => {
    setTagPage(1);
  }, [tagSearch, tagFilters, tagPageSize, selectedKey]);

  const tagFilterOptions: FilterConfig[] = useMemo(
    () => [
      {
        field: 'online',
        label: 'Online',
        type: 'select',
        options: [
          { label: 'Online', value: 'online' },
          { label: 'Offline', value: 'offline' },
        ],
      },
      {
        field: 'lowBattery',
        label: 'Battery',
        type: 'select',
        options: [{ label: 'Low battery (≤20%)', value: 'low' }],
      },
      {
        field: 'positionSource',
        label: 'Source',
        type: 'select',
        options: [
          { label: 'Cnapse UWB', value: 'uwb' },
          { label: 'Vendor integration', value: 'vendor' },
        ],
      },
    ],
    [],
  );

  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    return tags.filter((tag) => {
      if (tagFilters.online === 'online' && !tag.online) return false;
      if (tagFilters.online === 'offline' && tag.online) return false;
      if (
        tagFilters.lowBattery === 'low'
        && (tag.battery == null || tag.battery > LOW_BATTERY_THRESHOLD)
      ) {
        return false;
      }
      if (tagFilters.positionSource === 'vendor' && !isVendorTag(tag)) return false;
      if (tagFilters.positionSource === 'uwb' && !isUwbTag(tag)) return false;
      if (q) {
        const hay = [
          tag.serialNumber,
          formatTagFleetSourceLabel(tag.positionSource, tag.vendorType),
          ...(showDeviceMetadata
            ? [tag.manufacturerName ?? '', tag.deviceModel ?? '', tag.firmwareVersion ?? '']
            : []),
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tags, tagSearch, tagFilters, showDeviceMetadata]);

  const pagedTags = useMemo(() => {
    const start = (tagPage - 1) * tagPageSize;
    return filteredTags.slice(start, start + tagPageSize);
  }, [filteredTags, tagPage, tagPageSize]);

  const tagSourceCounts = useMemo(() => {
    let uwb = 0;
    let vendor = 0;
    for (const tag of tags) {
      if (isVendorTag(tag)) vendor += 1;
      else uwb += 1;
    }
    return { uwb, vendor };
  }, [tags]);

  const tagColumns: TableColumn<SiteTagFleetTagRow>[] = useMemo(
    () => [
      { field: 'serialNumber', headerName: 'Node SN', width: 130 },
      {
        field: 'positionSource',
        headerName: 'Source',
        width: 140,
        renderCell: (row) => {
          const label = formatTagFleetSourceLabel(row.positionSource, row.vendorType);
          const vendor = isVendorTag(row);
          return (
            <Tooltip
              title={
                vendor
                  ? 'Position from a third-party vendor integration (REST poll), not Cnapse Matrix UWB'
                  : 'Position from Cnapse Matrix UWB edge telemetry'
              }
            >
              <Chip
                size="small"
                label={label}
                color={vendor ? 'info' : 'default'}
                variant={vendor ? 'filled' : 'outlined'}
              />
            </Tooltip>
          );
        },
      },
      ...(showDeviceMetadata
        ? ([
            {
              field: 'manufacturerName',
              headerName: 'Manufacturer',
              width: 120,
              minWidth: 0,
              renderCell: (row: SiteTagFleetTagRow) => <MetadataCell text={row.manufacturerName} />,
            },
            {
              field: 'deviceModel',
              headerName: 'Model',
              width: 110,
              minWidth: 0,
              renderCell: (row: SiteTagFleetTagRow) => <MetadataCell text={row.deviceModel} />,
            },
            {
              field: 'firmwareVersion',
              headerName: 'Firmware',
              width: 110,
              minWidth: 0,
              renderCell: (row: SiteTagFleetTagRow) => <MetadataCell text={row.firmwareVersion} />,
            },
          ] satisfies TableColumn<SiteTagFleetTagRow>[])
        : []),
      {
        field: 'online',
        headerName: 'Online',
        width: 100,
        align: 'center',
        renderCell: (row) => (
          <Chip
            size="small"
            label={row.online ? 'Online' : 'Offline'}
            color={row.online ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'battery',
        headerName: 'Battery',
        width: 90,
        align: 'center',
        renderCell: (row) => {
          if (row.battery == null) return '-';
          const low = row.battery <= LOW_BATTERY_THRESHOLD;
          return (
            <Chip
              size="small"
              label={`${row.battery}%`}
              color={low ? 'warning' : 'default'}
              variant={low ? 'filled' : 'outlined'}
            />
          );
        },
      },
      {
        field: 'sosActive',
        headerName: 'SOS',
        width: 80,
        align: 'center',
        renderCell: (row) => (row.sosActive ? <Chip size="small" label="SOS" color="error" /> : '-'),
      },
      ...(siteZoneAlarmsEnabled
        ? ([
            {
              field: 'geofencingEnabled',
              headerName: showDeviceMetadata ? 'Tag geofencing' : 'Zone alarms',
              width: showDeviceMetadata ? 130 : 120,
              align: 'center' as const,
              renderCell: (row: SiteTagFleetTagRow) => {
                const tagEnabled = row.geofencingEnabled === true;
                const vendorTag = isVendorTag(row);
                if (showDeviceMetadata) {
                  return (
                    <Tooltip
                      title={
                        siteAllTagsZoneAlarms && !tagEnabled
                          ? 'Tenant geofencing is off; site dev override still arms all tags for zone alarms'
                          : tagEnabled
                            ? 'Tenant geofencing enabled for this tag'
                            : 'Tenant geofencing disabled for this tag'
                      }
                    >
                      <Chip
                        size="small"
                        label={tagEnabled ? 'On' : 'Off'}
                        color={tagEnabled ? 'primary' : 'default'}
                        variant={tagEnabled ? 'filled' : 'outlined'}
                        icon={<NotificationsIcon fontSize="small" />}
                      />
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip
                    title={
                      siteAllTagsZoneAlarms && !tagEnabled
                        ? 'Site dev override arms all tags for zone alarms regardless of this toggle'
                        : vendorTag
                          ? 'Zone alarms on this tag use position polled from the vendor integration'
                          : tagEnabled
                            ? 'Zone geofencing enabled for this tag'
                            : 'Zone geofencing disabled for this tag'
                    }
                  >
                    <span>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Switch
                            size="small"
                            checked={tagEnabled}
                            disabled={geofencingBusySerial === row.serialNumber}
                            onChange={(e) => { void handleGeofencingToggle(row, e.target.checked); }}
                          />
                        )}
                        label={tagEnabled ? (
                          <NotificationsIcon fontSize="small" color="primary" />
                        ) : (
                          <span />
                        )}
                      />
                    </span>
                  </Tooltip>
                );
              },
            },
          ] satisfies TableColumn<SiteTagFleetTagRow>[])
        : []),
      {
        field: 'lastSeenAt',
        headerName: 'Last seen',
        width: 130,
        renderCell: (row) => {
          if (!row.lastSeenAt) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const absolute = formatDateTime(row.lastSeenAt);
          const relative = formatRelativeTime(row.lastSeenAt);
          return (
            <Tooltip title={absolute ? `Last seen at ${absolute}` : undefined}>
              <Typography variant="body2" component="span">
                {relative || '—'}
              </Typography>
            </Tooltip>
          );
        },
      },
    ],
    [showDeviceMetadata, siteZoneAlarmsEnabled, siteAllTagsZoneAlarms, geofencingBusySerial, handleGeofencingToggle],
  );

  const siteListPane = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <AppTextField
        size="small"
        placeholder="Search sites..."
        value={siteSearch}
        onChange={(e) => setSiteSearch(e.target.value)}
        sx={{ mb: 1.5, flexShrink: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pr: 0.5,
          ...scrollbarSx(theme),
        }}
      >
        {sitesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredSites.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
            No sites match your search.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filteredSites.map((site) => (
              <SiteTagFleetCard
                key={siteKey(site)}
                site={site}
                selected={siteKey(site) === selectedKey}
                tenantLabel={
                  showTenantOnCards ? (tenantNameById?.(site.tenantId) ?? site.tenantId) : null
                }
                onSelect={() => setSelectedKey(siteKey(site))}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );

  const tagTablePane = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      {!selectedSite ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Select a site to view Node details.
          </Typography>
        </Box>
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1.5, flexShrink: 0, minWidth: 0, rowGap: 0.25 }}
          >
            <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
              {selectedSite.siteName ?? selectedSite.siteId}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
              {selectedSite.tagsSeenCount} Nodes seen
              {tags.length > 0
                ? ` · ${tagSourceCounts.uwb} Cnapse Node · ${tagSourceCounts.vendor} vendor`
                : ''}
            </Typography>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <EnhancedTable
              data={pagedTags}
              totalCount={filteredTags.length}
              columns={tagColumns}
              getRowId={(row) => row.serialNumber}
              page={tagPage}
              pageSize={tagPageSize}
              onPageChange={setTagPage}
              onPageSizeChange={setTagPageSize}
              loading={tagsLoading}
              searchable
              searchTerm={tagSearch}
              onSearchChange={setTagSearch}
              searchPlaceholder={
                showDeviceMetadata
                  ? 'Search serial, source, manufacturer, model, firmware...'
                  : 'Search Node serial or source...'
              }
              filters={tagFilters}
              filterOptions={tagFilterOptions}
              onFilterChange={(filters) => setTagFilters(filters as Record<string, string>)}
              onRefresh={
                selectedSite
                  ? () => {
                      void fetchTags(selectedSite);
                    }
                  : undefined
              }
              refreshTitle="Refresh selected site"
              maxHeight="100%"
              emptyStateTitle="No Nodes"
              emptyStateDescription={
                tags.length === 0
                  ? 'No runtime Node state for this site.'
                  : 'No Nodes match your search or filters.'
              }
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <TableDetailSplitLayout
      sx={{ flex: 1, minHeight: { xs: 'auto', lg: 480 }, minWidth: 0 }}
      tableWidth={SITE_LIST_DEFAULT_WIDTH}
      minTableWidth={SITE_LIST_MIN_WIDTH}
      maxTableWidth={SITE_LIST_MAX_WIDTH}
      resizable
      table={siteListPane}
      panel={tagTablePane}
    />
  );
}
